import { AxiosResponse } from 'axios';
import { axiosRequest } from '@/common/api/instance';
import { Meta, PaginatedResponse, Response as ApiResponse } from '@/types/api/response.types';
import {
  AddProjectUserBody,
  AddProjectUserResponse,
  BOQSummary,
  Cashflow,
  CashflowGraph,
  CashflowParams,
  CreateProjectBody,
  Project,
  ProjectDates,
  ProjectImageBody,
  ProjectImageResponse,
  UpdateProjectBody,
} from '@/types/api/project.types';
import { ProjectParams } from '@/types/api/organisations.types';
import { ErfNumberValidationParams, Site, SiteParams } from '@/types/api/sites.types';

const resource = '/projects';

export const createProject = async (
  data: CreateProjectBody,
): Promise<AxiosResponse<ApiResponse<Project>>> => {
  return axiosRequest().post(`${resource}/`, data);
};

export const updateProject = async (
  id: string,
  data: UpdateProjectBody,
): Promise<AxiosResponse<ApiResponse<Project>>> => {
  return axiosRequest().put(`${resource}/${id}`, data);
};

export const getProject = async (id: string): Promise<AxiosResponse<ApiResponse<Project>>> => {
  return axiosRequest().get(`${resource}/${id}`);
};

export const uploadImage = async (
  data: ProjectImageBody,
): Promise<AxiosResponse<ApiResponse<ProjectImageResponse>>> => {
  return axiosRequest().patch(`${resource}/${data.id}/images`, data.body);
};

export const deleteImage = async (id: string): Promise<AxiosResponse<ApiResponse<Meta>>> => {
  return axiosRequest().delete(`${resource}/${id}/images`);
};

export const removeUser = async (
  projectRoleId: string,
): Promise<AxiosResponse<ApiResponse<Meta>>> => {
  return axiosRequest().delete(`${resource}/users/${projectRoleId}`);
};

export const addUsers = async (
  id: string,
  data: AddProjectUserBody[],
): Promise<AxiosResponse<ApiResponse<AddProjectUserResponse>>> => {
  return axiosRequest().post(`${resource}/${id}/users`, data);
};

export const getProjectSites = async (
  params: SiteParams,
): Promise<AxiosResponse<PaginatedResponse<Site[]>>> => {
  return axiosRequest().get(`${resource}/${params.id}/sites`, { params });
};

export const getProjectDates = async (
  id: string,
): Promise<AxiosResponse<ApiResponse<ProjectDates>>> => {
  return axiosRequest().get(`${resource}/${id}/dates`);
};

export const getProjectCashflow = async (
  id: string,
  params: CashflowParams,
): Promise<AxiosResponse<ApiResponse<Cashflow>>> => {
  return axiosRequest().get(`${resource}/${id}/cashflows`, { params });
};

export const getProjectCashflowGraph = async (
  id: string,
  params: CashflowParams,
): Promise<AxiosResponse<ApiResponse<CashflowGraph>>> => {
  return axiosRequest().get(`${resource}/${id}/cashflows/cumulative-net`, { params });
};

export const updateProjectClaimBannerDisplay = async (
  id: string,
): Promise<AxiosResponse<ApiResponse<Project>>> => {
  return axiosRequest().patch(`${resource}/${id}/cashflows/banner`);
};

export const getProjectBOQSummaries = async (
  params: ProjectParams,
): Promise<AxiosResponse<PaginatedResponse<BOQSummary[]>>> => {
  return axiosRequest().get(`${resource}/${params.id}/boqs/summaries`, { params });
};

export const exportProjectCashflow = (id: string): Promise<AxiosResponse<Blob>> => {
  return axiosRequest().get(`${resource}/${id}/cashflows/export`, { responseType: 'blob' });
};

export const validateErfNumber = async (
  params: ErfNumberValidationParams,
): Promise<AxiosResponse<ApiResponse<boolean>>> => {
  return axiosRequest().get(`${resource}/${params.projectId}/validate/erf-number`, { params });
};
